import React, { useEffect, useState } from "react";
import { ActivityIndicator, StyleSheet, View, TouchableOpacity } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { MotiView } from "moti";
import { useDebounce } from "use-debounce";

import AdminTable from "../../components/adminDashboard/AdminTable/AdminTable";
import AdminTableHeaderWithCourseFilter from "../../components/adminDashboard/AdminTable/AdminTableHeaderWithCourseFilter";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../components/common/general/CustomText/CustomText";
import {
  useGetSessionAnalyticsCsvQuery,
  useGetSessionAnalyticsCsvStatusQuery,
  useGetUserSessionAnalyticsQuery,
  useGetCourseSessionAnalyticsQuery,
  useGetUserChatMessageAnalyticsQuery,
} from "../../graphql/generated/graphql";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import CustomModal from "../../components/common/general/CustomModal/CustomModal";
import type { RootStackParamList } from "../../navigation/navigation.types";
import useSortingDropdown from "../../hooks/useSortingDropdown";
import usePagination from "../../hooks/usePagination";
import AdminTablePagination from "../../components/adminDashboard/AdminTable/AdminTablePagination";
import UserSessionAnalyticsTable from "../../components/adminDashboard/Analytics/UserSessionAnalyticsTable";
import UserChatMessageAnalyticsTable from "../../components/adminDashboard/Analytics/UserChatMessageAnalyticsTable";
import CourseAnalyticsSection from "./CourseAnalyticsSection";
import DefaultLayout from "../../layouts/DefaultLayout.web";
import useFileFetch from "../../hooks/useFileFetch";

const SORT_ITEMS = [
  { label: "Name (A-Z)", value: "name_asc" },
  { label: "Name (Z-A)", value: "name_desc" },
];

const CHAT_SORT_ITEMS = [
  { label: "Name (A-Z)", value: "name_asc" },
  { label: "Name (Z-A)", value: "name_desc" },
];

type AnalyticsTab = "session" | "chat";

const SessionAnalyticsView: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const [activeTab, setActiveTab] = useState<AnalyticsTab>("session");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const [exportError, setExportError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [analyticsView, setAnalyticsView] = useState<"users" | "courses">("users");
  const [coursesOnly, setCoursesOnly] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [chatCoursesOnly, setChatCoursesOnly] = useState(false);
  const [selectedChatCourses, setSelectedChatCourses] = useState<string[]>([]);

  const { fetchAndOpenFile } = useFileFetch();

  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "name_asc",
  });

  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: 10 });

  const {
    data: usersData,
    loading: sessionLoading,
    refetch: usersRefetch,
  } = useGetUserSessionAnalyticsQuery({
    variables: {
      input: {
        searchQuery: analyticsView === "courses" ? "" : debouncedSearchQuery,
        page: analyticsView === "courses" ? 1 : page,
        pageSize: analyticsView === "courses" ? 1000 : pageSize,
        sortBy: analyticsView === "courses" ? "name_asc" : sortBy,
        courseFilter: analyticsView === "courses" ? [] : selectedCourses,
        coursesOnly: analyticsView === "courses" ? false : coursesOnly,
      },
    },
    skip: activeTab !== "session",
  });

  const {
    data: coursesData,
    loading: coursesLoading,
    refetch: coursesRefetch,
  } = useGetCourseSessionAnalyticsQuery({
    skip: activeTab !== "session",
  });

  const {
    data: chatUsersData,
    loading: chatLoading,
    refetch: chatUsersRefetch,
  } = useGetUserChatMessageAnalyticsQuery({
    variables: {
      input: {
        searchQuery: debouncedSearchQuery,
        page,
        pageSize,
        sortBy,
        courseFilter: selectedChatCourses,
        coursesOnly: chatCoursesOnly,
      },
    },
    skip: activeTab !== "chat",
  });

  // CSV Export Status Query
  const {
    data: statusData,
    startPolling,
    stopPolling,
  } = useGetSessionAnalyticsCsvStatusQuery({
    variables: { jobId: currentJobId || "" },
    skip: !currentJobId,
  });

  const { refetch: getSessionAnalyticsCsvRefetch } = useGetSessionAnalyticsCsvQuery({
    skip: true,
  });

  const handleExportCsv = async () => {
    try {
      setExportError(null);
      const { data } = await getSessionAnalyticsCsvRefetch();
      const response = data?.getSessionAnalyticsCsv;

      if (response?.success && response?.jobId) {
        setCurrentJobId(response.jobId);
        setShowProcessingModal(true);
        startPolling(2000);
      } else {
        setExportError(response?.message || "Failed to start CSV generation. Please try again.");
        setShowErrorModal(true);
      }
    } catch (e) {
      setExportError("An error occurred while starting CSV generation. Please try again.");
      setShowErrorModal(true);
    }
  };

  const users = usersData?.getUserSessionAnalytics?.data || [];
  const totalCount = usersData?.getUserSessionAnalytics?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  const courseTotalCount = coursesData?.getCourseSessionAnalytics?.totalCount || 0;

  const chatUsers = chatUsersData?.getUserChatMessageAnalytics?.data || [];
  const chatTotalCount = chatUsersData?.getUserChatMessageAnalytics?.totalCount || 0;
  const chatTotalPages = Math.ceil(chatTotalCount / pageSize);

  const availableCourses = React.useMemo(() => {
    const courseSet = new Set<string>();
    users.forEach((user) => {
      if (user.courses) {
        user.courses.forEach((course) => {
          courseSet.add(course.id);
        });
      }
    });
    return Array.from(courseSet).map((courseId) => {
      const courseName =
        users.flatMap((user) => user.courses || []).find((course) => course.id === courseId)?.name || courseId;

      return {
        key: courseId,
        value: courseName,
      };
    });
  }, [users]);

  const availableChatCourses = React.useMemo(() => {
    const courses = chatUsersData?.getUserChatMessageAnalytics?.availableCourses || [];
    return courses.map((course) => ({
      key: course.id,
      value: course.name,
    }));
  }, [chatUsersData]);

  useEffect(() => {
    setPage(1);
  }, [
    searchQuery,
    sortBy,
    coursesOnly,
    selectedCourses,
    analyticsView,
    activeTab,
    chatCoursesOnly,
    selectedChatCourses,
  ]);

  useEffect(() => {
    if (activeTab === "session") {
      usersRefetch();
      coursesRefetch();
    } else if (activeTab === "chat") {
      chatUsersRefetch();
    }
  }, [
    debouncedSearchQuery,
    page,
    pageSize,
    sortBy,
    coursesOnly,
    selectedCourses,
    analyticsView,
    activeTab,
    chatCoursesOnly,
    selectedChatCourses,
  ]);

  useEffect(() => {
    if (statusData?.getSessionAnalyticsCsvStatus) {
      const status = statusData.getSessionAnalyticsCsvStatus;

      if (status.success && status.downloadUrl) {
        stopPolling();
        setShowProcessingModal(false);
        setCurrentJobId(null);

        fetchAndOpenFile(status.downloadUrl);
      } else if (!status.success) {
        stopPolling();
        setShowProcessingModal(false);
        setCurrentJobId(null);
        setExportError(status.message || "CSV generation failed. Please try again.");
        setShowErrorModal(true);
      }
    }
  }, [statusData, stopPolling, activeTab]);

  const handleUserSelect = (userId: string) => {
    navigation.navigate("User Session Details", {
      userId,
    });
  };

  const getCurrentLoading = () => {
    if (activeTab === "session") {
      return analyticsView === "courses" ? coursesLoading : sessionLoading;
    }
    return chatLoading;
  };

  const getCurrentTotalCount = () => {
    if (activeTab === "session") {
      return analyticsView === "courses" ? courseTotalCount : totalCount;
    }
    return chatTotalCount;
  };

  const getCurrentTotalPages = () => {
    if (activeTab === "session") {
      return totalPages;
    }
    return chatTotalPages;
  };

  const getCurrentData = () => {
    if (activeTab === "session") {
      return users;
    }
    return chatUsers;
  };

  const renderSessionAnalytics = () => {
    if (analyticsView === "courses") {
      return (
        <CourseAnalyticsSection
          coursesData={coursesData}
          loading={coursesLoading}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortDropdownOpen={sortDropdownOpen}
          setSortDropdownOpen={setSortDropdownOpen}
        />
      );
    }

    return (
      <>
        <AdminTableHeaderWithCourseFilter
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortDropdownOpen={sortDropdownOpen}
          setSortDropdownOpen={setSortDropdownOpen}
          sortItems={SORT_ITEMS}
          coursesOnly={coursesOnly}
          setCoursesOnly={setCoursesOnly}
          availableCourses={availableCourses}
          selectedCourses={selectedCourses}
          setSelectedCourses={setSelectedCourses}
          showCourseFilter={true}
        />
        {sessionLoading ? (
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color="#0084FF" />
          </View>
        ) : users.length > 0 ? (
          <UserSessionAnalyticsTable users={users} onUserSelect={handleUserSelect} />
        ) : (
          <View style={styles.emptyState}>
            <CustomText text="No users found." />
          </View>
        )}
        {totalCount > 0 && (
          <AdminTablePagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
          />
        )}
      </>
    );
  };

  const renderChatAnalytics = () => {
    return (
      <>
        <AdminTableHeaderWithCourseFilter
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          sortBy={sortBy}
          setSortBy={setSortBy}
          sortDropdownOpen={sortDropdownOpen}
          setSortDropdownOpen={setSortDropdownOpen}
          sortItems={CHAT_SORT_ITEMS}
          coursesOnly={chatCoursesOnly}
          setCoursesOnly={setChatCoursesOnly}
          availableCourses={availableChatCourses}
          selectedCourses={selectedChatCourses}
          setSelectedCourses={setSelectedChatCourses}
          showCourseFilter={true}
          toggleLeftText="All Chats"
          toggleRightText="Course Chats Only"
        />
        {chatLoading ? (
          <View style={styles.loaderContainer}>
            <ActivityIndicator size="large" color="#0084FF" />
          </View>
        ) : chatUsers.length > 0 ? (
          <UserChatMessageAnalyticsTable users={chatUsers} onUserSelect={handleUserSelect} />
        ) : (
          <View style={styles.emptyState}>
            <CustomText text="No users found." />
          </View>
        )}
        {chatTotalCount > 0 && (
          <AdminTablePagination
            currentPage={page}
            totalPages={chatTotalPages}
            totalItems={chatTotalCount}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onItemsPerPageChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
          />
        )}
      </>
    );
  };

  return (
    <DefaultLayout pageHasChat={false}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <MotiView
            from={{ opacity: 0, translateY: 20, scale: 1 }}
            animate={{ opacity: 1, translateY: 0, scale: 1 }}
            delay={10}
            // @ts-expect-error - transition is not typed
            transition={{ type: "timing", duration: 500 }}>
            <View style={styles.header}>
              <View style={styles.headerLeft}>
                <CustomText
                  text="Session Analytics"
                  size="m"
                  textType="display"
                  weight="ultraBold"
                  style={styles.title}
                />
                <CustomText text="View session and chat analytics for all users." size="m" style={styles.subtitle} />
              </View>
              <View>
                <CustomButton
                  text={"Export CSV"}
                  onPress={handleExportCsv}
                  styleType="primaryTransparentStroke"
                  disabled={false}
                />
                <CustomSpacing type="vertical" size="xs" />
                <CustomText
                  text="Data available for the last 90 days only"
                  size="xs"
                  style={{ color: "#6B7280" }}
                  useTranslationText={false}
                />
              </View>
            </View>

            <CustomSpacing type="vertical" size="m" />

            <View style={styles.tabContainer}>
              <View style={styles.tabRow}>
                <TouchableOpacity
                  style={[styles.tab, activeTab === "session" && styles.activeTab]}
                  onPress={() => setActiveTab("session")}>
                  <CustomText text="Session Analytics" weight={activeTab === "session" ? "bold" : "normal"} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.tab, activeTab === "chat" && styles.activeTab]}
                  onPress={() => setActiveTab("chat")}>
                  <CustomText text="Chat Message Analytics" weight={activeTab === "chat" ? "bold" : "normal"} />
                </TouchableOpacity>
              </View>
            </View>

            <CustomSpacing type="vertical" size="l" />

            {activeTab === "session" && (
              <View style={styles.totalCountContainer}>
                <View style={styles.toggleContainer}>
                  <TouchableOpacity
                    style={[
                      styles.toggleButton,
                      styles.toggleButtonLeft,
                      analyticsView === "users" && styles.toggleButtonActiveRight,
                    ]}
                    onPress={() => setAnalyticsView("users")}>
                    <CustomText
                      text={`Total Users (${sessionLoading ? "-" : totalCount})`}
                      size="s"
                      weight={analyticsView === "users" ? "bold" : "normal"}
                      style={[styles.toggleText, analyticsView === "users" && styles.toggleTextActive]}
                      useTranslationText={false}
                    />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.toggleButton,
                      styles.toggleButtonRight,
                      analyticsView === "courses" && styles.toggleButtonActiveLeft,
                    ]}
                    onPress={() => setAnalyticsView("courses")}>
                    <CustomText
                      text={`Total Courses (${coursesLoading ? "-" : courseTotalCount})`}
                      size="s"
                      weight={analyticsView === "courses" ? "bold" : "normal"}
                      style={[styles.toggleText, analyticsView === "courses" && styles.toggleTextActive]}
                      useTranslationText={false}
                    />
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {activeTab === "chat" && (
              <View style={styles.totalCountContainer}>
                <CustomText
                  text={`Total Users (${chatLoading ? "-" : chatTotalCount})`}
                  size="s"
                  weight="normal"
                  style={styles.totalCountText}
                  useTranslationText={false}
                />
              </View>
            )}

            <CustomSpacing type="vertical" size="m" />

            <AdminTable>{activeTab === "session" ? renderSessionAnalytics() : renderChatAnalytics()}</AdminTable>
          </MotiView>
        </View>
      </View>

      <CustomModal
        visible={showErrorModal}
        onRequestClose={() => setShowErrorModal(false)}
        showCloseButton={true}
        title="Export Error"
        style={styles.errorModal}>
        <View style={styles.errorModalContent}>
          <CustomText
            text={exportError || "An error occurred while exporting the CSV. Please try again."}
            size="m"
            useTranslationText={false}
            style={styles.errorText}
          />
          <CustomSpacing type="vertical" size="l" />
          <CustomButton text="Close" onPress={() => setShowErrorModal(false)} styleType="primary" />
        </View>
      </CustomModal>

      <CustomModal
        visible={showProcessingModal}
        onRequestClose={() => {
          setShowProcessingModal(false);
          setCurrentJobId(null);
          stopPolling();
        }}
        showCloseButton={false}
        title="Preparing CSV Export"
        style={styles.processingModal}>
        <View style={styles.processingModalContent}>
          <ActivityIndicator size="large" color="#0084FF" />
          <CustomSpacing type="vertical" size="m" />
          <CustomText
            text="Your CSV file is being prepared. This may take a few moments..."
            size="m"
            useTranslationText={false}
            style={styles.processingText}
          />
          <CustomSpacing type="vertical" size="l" />
        </View>
      </CustomModal>
    </DefaultLayout>
  );
};

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  pageBackground: {
    flex: 1,
    // @ts-expect-error - works on web
    minHeight: "100vh",
  },
  contentContainer: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flex: 1,
  },
  title: {
    color: "#101828",
    marginBottom: 4,
  },
  subtitle: {
    color: "#6B7280",
  },
  totalCountContainer: {
    width: "100%",
    marginBottom: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  totalCountText: {
    color: "#111827",
  },
  toggleContainer: {
    flexDirection: "row",
    backgroundColor: "#F3F4F6",
    borderRadius: 8,
    padding: 4,
  },
  toggleButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    minWidth: 120,
  },
  toggleButtonLeft: {
    marginRight: 2,
  },
  toggleButtonRight: {
    marginLeft: 2,
  },
  toggleButtonActiveLeft: {
    backgroundColor: "#FFFFFF",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleButtonActiveRight: {
    backgroundColor: "#FFFFFF",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  toggleText: {
    color: "#6B7280",
  },
  toggleTextActive: {
    color: "#111827",
  },
  errorModal: {
    width: "90%",
    maxWidth: 400,
  },
  errorModalContent: {
    padding: 16,
  },
  errorText: {
    color: "#DC2626",
    textAlign: "center",
  },
  processingModal: {
    width: "90%",
    maxWidth: 400,
  },
  processingModalContent: {
    padding: 16,
    alignItems: "center",
  },
  processingText: {
    color: "#6B7280",
    textAlign: "center",
  },
  tabContainer: {
    marginVertical: 16,
  },
  tabRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e5e5e5",
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: "center",
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
    cursor: "pointer",
  },
  activeTab: {
    borderBottomColor: "#0084FF",
  },
});

export default SessionAnalyticsView;
